import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import colors from '../../design-system/colors';
import spacing from '../../design-system/spacing';
import shadows from '../../design-system/shadows';

/**
 * TeamCard - Card de equipo con escudo, récord W-D-L y racha
 * Para mostrar información resumida de equipos
 */

const StyledCard = styled(motion.div)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: spacing.borderRadius.lg,
  padding: spacing[3],
  boxShadow: shadows.card,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  overflow: 'hidden',

  '&:hover': {
    boxShadow: shadows.cardHover,
    transform: 'translateY(-2px)',
    borderColor: colors.primary[300],

    '& .team-shield': {
      transform: 'scale(1.1) rotate(5deg)',
    },
  },
}));

const RecordBadge = styled(Box)(({ type }) => ({
  flex: 1,
  textAlign: 'center',
  padding: `${spacing[1]}px ${spacing[2]}px`,
  borderRadius: spacing.borderRadius.md,
  backgroundColor: 
    type === 'win' ? colors.football.win + '15' :
    type === 'draw' ? colors.football.draw + '15' :
    colors.football.loss + '15',
}));

const FormDot = styled(Box)(({ result }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700,
  color: colors.neutral.white,
  backgroundColor: 
    result === 'V' ? colors.football.win :
    result === 'E' ? colors.football.draw :
    colors.football.loss,
}));

const TeamCard = ({
  nombre,
  escudo,
  posicion,
  puntos,
  ganados = 0,
  empatados = 0,
  perdidos = 0,
  golesFavor = 0,
  golesContra = 0,
  ultimos5 = [], // Array: ['V', 'E', 'P', 'V', 'V']
  racha, // 'up' o 'down' o null
  onClick,
}) => {
  const partidosJugados = ganados + empatados + perdidos;
  const diferencia = golesFavor - golesContra;

  return (
    <StyledCard
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Header con posición y nombre */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {/* Posición */}
        {posicion && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: spacing.borderRadius.md,
              backgroundColor: posicion <= 4 ? colors.primary[500] : colors.neutral[500],
              color: colors.neutral.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            {posicion}
          </Box>
        )}

        {/* Escudo */}
        <Avatar
          src={escudo}
          className="team-shield"
          sx={{
            width: 48,
            height: 48,
            transition: 'transform 0.3s ease',
            backgroundColor: 'action.selected',
          }}
        >
          {nombre?.substring(0, 2).toUpperCase()}
        </Avatar>

        {/* Nombre y puntos */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nombre}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h5" fontWeight={700} color={colors.primary[700]}>
              {puntos}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              puntos
            </Typography>
          </Box>
        </Box>

        {/* Racha indicator */}
        {racha && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: racha === 'up' ? colors.football.win : colors.football.loss,
            }}
          >
            {racha === 'up' ? <TrendingUp /> : <TrendingDown />}
          </Box>
        )}
      </Box>

      {/* Récord W-D-L */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <RecordBadge type="win">
          <Typography variant="h6" fontWeight={700} color={colors.football.win}>
            {ganados}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            G
          </Typography>
        </RecordBadge>
        <RecordBadge type="draw">
          <Typography variant="h6" fontWeight={700} color={colors.football.draw}>
            {empatados}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            E
          </Typography>
        </RecordBadge>
        <RecordBadge type="loss">
          <Typography variant="h6" fontWeight={700} color={colors.football.loss}>
            {perdidos}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            P
          </Typography>
        </RecordBadge>
      </Box>

      {/* Stats adicionales */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
          mb: 2,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            PJ
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {partidosJugados}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            GF-GC
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {golesFavor}-{golesContra}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            DIF
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color={diferencia > 0 ? colors.football.win : diferencia < 0 ? colors.football.loss : 'text.primary'}
          >
            {diferencia > 0 ? '+' : ''}{diferencia}
          </Typography>
        </Box>
      </Box>

      {/* Forma (últimos 5 partidos) */}
      {ultimos5.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
          >
            Últimos 5 partidos
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            {ultimos5.slice(-5).map((result, index) => (
              <FormDot key={index} result={result}>
                {result === 'V' ? '✓' : result === 'E' ? '−' : '✗'}
              </FormDot>
            ))}
          </Box>
        </Box>
      )}
    </StyledCard>
  );
};

export default TeamCard;
